/**
 * Casting abstraction — Chromecast / AirPlay / TV / native adapters.
 *
 * PlaybackEngine must never import casting SDKs; host apps use CastingManager
 * or inject a provider via createCastingProvider(kind).
 */

import { createChromecastProvider } from "./Casting/ChromecastProvider";
import { createAirPlayProvider } from "./Casting/AirPlayProvider";
import { createRokuProvider } from "./Casting/RokuProvider";
import { createFireTVProvider } from "./Casting/FireTVProvider";
import { createSamsungTVProvider } from "./Casting/SamsungTVProvider";
import { createLGTVProvider } from "./Casting/LGTVProvider";
import { createFutureNativeProvider } from "./Casting/FutureNativeProvider";
import { createSimulatedCastProvider } from "./Casting/SimulatedCastProvider";
import { createNullCastingProvider } from "./Casting/NullCastingProvider";
import { createScreenMirrorProvider } from "./Casting/ScreenMirrorProvider";

export {
  CastingEvent,
  CastingTarget,
  CastingConnectionState,
  CastingCapabilityState,
  CastingUiState,
} from "./Casting/CastingEvents";
export { createCastingManager } from "./Casting/CastingManager";
export {
  getAppCastingSession,
  ensureAppCastingSession,
} from "./Casting/AppCastingSession";
export { createChromecastProvider } from "./Casting/ChromecastProvider";
export { createAirPlayProvider, detectAirPlayEnvironment } from "./Casting/AirPlayProvider";
export { createRokuProvider } from "./Casting/RokuProvider";
export { createFireTVProvider } from "./Casting/FireTVProvider";
export { createSamsungTVProvider } from "./Casting/SamsungTVProvider";
export { createLGTVProvider } from "./Casting/LGTVProvider";
export { createFutureNativeProvider } from "./Casting/FutureNativeProvider";
export { createSimulatedCastProvider } from "./Casting/SimulatedCastProvider";
export { createNullCastingProvider } from "./Casting/NullCastingProvider";
export {
  createScreenMirrorProvider,
  detectScreenMirrorEnvironment,
  detectHostEnvironment,
  buildScreenMirrorInstructions,
  resolveOsHandoffTarget,
  attemptOsUriHandoff,
  WINDOWS_WIRELESS_DISPLAY_URI,
  ANDROID_CAST_SETTINGS_INTENT,
  ScreenMirrorMode,
} from "./Casting/ScreenMirrorProvider";
export { loadCastFramework } from "./Casting/ChromecastProvider";
export {
  CastProviderId,
  CastContentType,
  CastReceiverPlaybackState,
  CastReceiverExperienceSurfaces,
  CastPlatformReceiverRequirements,
  normalizeWhiteLabelCastConfig,
  createCastReceiverSession,
  createReceiverControlMessage,
  isReallyConnectedCapability,
  buildPlatformStatusRow,
} from "./Casting/CastReceiverContract";
export {
  createCloudCastSessionClient,
  buildCloudCastSessionCreate,
  buildRokuEcpDeepLinkUrl,
  buildRokuDeepLinkParams,
  CLOUD_CAST_API_PATHS,
} from "./Casting/CloudCastSession";

/**
 * Factory for casting adapters.
 * @param {string} kind
 * @param {object} [options]
 * @returns {object}
 */
export function createCastingProvider(kind, options) {
  options = options || {};
  if (kind === "chromecast") {
    return createChromecastProvider(options);
  }
  if (kind === "airplay") {
    return createAirPlayProvider(options);
  }
  if (kind === "roku") {
    return createRokuProvider(options);
  }
  if (kind === "firetv" || kind === "fire_tv") {
    return createFireTVProvider(options);
  }
  if (kind === "samsung") {
    return createSamsungTVProvider(options);
  }
  if (kind === "lg") {
    return createLGTVProvider(options);
  }
  if (kind === "future_native" || kind === "native") {
    return createFutureNativeProvider(options);
  }
  if (kind === "simulated" || kind === "sim") {
    return createSimulatedCastProvider(options);
  }
  if (kind === "screen_mirror" || kind === "screenMirror") {
    return createScreenMirrorProvider(options);
  }
  return createNullCastingProvider();
}

export default createNullCastingProvider;
